import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { LifeBuoy, Plus, Send } from "lucide-react";

const ST_COLOR = { Open: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10", Answered: "text-primary border-primary/40 bg-primary/10", Resolved: "text-zinc-400 border-border bg-secondary" };

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "" });

  const load = () => api.get("/tickets").then(({ data }) => { setTickets(data); if (active) setActive(data.find((x) => x.id === active.id) || null); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openTicket = (t) => {
    setActive(t);
    if (t.awaiting_user) { api.post(`/tickets/${t.id}/read`).then(() => load()).catch(() => {}); }
  };

  const create = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Subject and message required"); return; }
    try { await api.post("/tickets", form); toast.success("Ticket submitted"); setOpen(false); setForm({ subject: "", message: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    await api.post(`/tickets/${active.id}/reply`, { message: reply }); setReply(""); load();
  };

  return (
    <div data-testid="support-page">
      <div className="flex items-end justify-between mb-8">
        <div><h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Help</h4><h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Support</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-ticket-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90"><Plus className="h-4 w-4 mr-2" />New Ticket</Button></DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Contact Support</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Subject</Label><Input data-testid="ticket-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 bg-secondary border-border" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Message</Label><Textarea data-testid="ticket-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-secondary border-border" rows={4} /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="submit-ticket-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 space-y-2">
          {tickets.length === 0 ? <div className="bg-card border border-border rounded-lg text-center py-14"><LifeBuoy className="h-10 w-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-500">No tickets yet.</p></div> :
            tickets.map((t) => (
              <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => openTicket(t)} className={`w-full text-left bg-card border rounded-lg p-4 transition-colors ${active?.id === t.id ? "border-primary/50" : "border-border hover:border-zinc-600"}`}>
                <div className="flex items-center justify-between"><div className="font-semibold text-sm flex items-center gap-2">{t.subject}{t.awaiting_user && <span data-testid={`ticket-unread-${t.id}`} className="h-2 w-2 rounded-full bg-primary" />}</div><span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${ST_COLOR[t.status]}`}>{t.status}</span></div>
                <div className="text-xs text-zinc-500 mt-1">{t.messages?.length || 0} messages</div>
              </button>))}
        </div>
        <div className="lg:col-span-3">
          {active ? (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="font-heading text-lg font-semibold mb-4">{active.subject}</div>
              <div className="space-y-3 mb-4 max-h-80 overflow-y-auto" data-testid="ticket-thread">
                {active.messages?.map((m, i) => (
                  <div key={i} className={`text-sm rounded-md px-4 py-2.5 ${m.sender === "user" ? "bg-secondary border border-border ml-8" : "bg-primary/10 border border-primary/30 mr-8"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{m.sender === "user" ? "You" : "Support"}</div>{m.text}
                  </div>))}
              </div>
              {active.status !== "Resolved" && <div className="flex gap-2"><Input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply..." className="bg-secondary border-border" /><Button onClick={sendReply} data-testid="ticket-reply-send" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90"><Send className="h-4 w-4" /></Button></div>}
            </div>
          ) : <div className="bg-card border border-border rounded-lg text-center py-20 text-zinc-500">Select a ticket or create a new one.</div>}
        </div>
      </div>
    </div>
  );
}
